import { useState, useEffect } from "react";
import { GoogleLogin } from "@react-oauth/google";
import "./AuthModal.css";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  forgotPassword,
  signin,
  signup,
  signuporingoogle,
} from "../../../actions/user";
import { addtonewsletter } from "../../../api";

const PASSWORD_RULES = [
  { id: "length", label: "8 or more characters", test: (p) => p.length >= 8 },
  {
    id: "letter",
    label: "At least one letter",
    test: (p) => /[a-zA-Z]/.test(p),
  },
  { id: "number", label: "At least one number", test: (p) => /\d/.test(p) },
  {
    id: "special",
    label: "At least one special character",
    test: (p) => /[^a-zA-Z0-9]/.test(p),
  },
];

/** @param {{ open: boolean }} props */
function EyeIcon({ open }) {
  return open ? (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/**
 * Authentication modal supporting sign-in and sign-up flows.
 * @param {{ initialMode: 'signin'|'signup', onClose: () => void }} props
 */
export default function AuthModal({ initialMode = "signin", onClose }) {
  const [mode, setMode] = useState(initialMode);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Sign-in fields
  const [siEmail, setSiEmail] = useState("");
  const [siPassword, setSiPassword] = useState("");
  const [siShowPw, setSiShowPw] = useState(false);

  // Forgot-password fields
  const [fpEmail, setFpEmail] = useState("");
  const [fpSubmitted, setFpSubmitted] = useState(false);

  // Sign-up fields
  const [suFirstName, setSuFirstName] = useState("");
  const [suLastName, setSuLastName] = useState("");
  const [suEmail, setSuEmail] = useState("");
  const [suPassword, setSuPassword] = useState("");
  const [suConfirm, setSuConfirm] = useState("");
  const [suShowPw, setSuShowPw] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Errors for form
  const [errors, setErrors] = useState({});
  // Error message for failed login
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const switchMode = (next, prefill = {}) => {
    setMode(next);
    setErrors({});
    setErrorMessage("");
    setFpSubmitted(false);
    if (prefill.fpEmail !== undefined) setFpEmail(prefill.fpEmail);
  };

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    if (!fpEmail.trim()) {
      setErrors({ fpEmail: "Email address is required" });
      return;
    }
    setErrors({});
    dispatch(forgotPassword(fpEmail, setErrorMessage));
    setFpSubmitted(true);
  };

  const pwChecks = PASSWORD_RULES.map((r) => ({
    ...r,
    passed: r.test(suPassword),
  }));
  const pwValid = pwChecks.every((c) => c.passed);

  const handleSignIn = (e) => {
    e.preventDefault();
    const errs = {};
    if (!siEmail.trim()) errs.siEmail = "Email is required";
    if (!siPassword) errs.siPassword = "Password is required";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    // Call sign-in API
    var formData = { email: siEmail, password: siPassword };
    console.log(formData);
    dispatch(signin(formData, onClose, navigate, setErrorMessage));
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!suFirstName.trim()) errs.suFirstName = "First name is required";
    if (!suLastName.trim()) errs.suLastName = "Last name is required";
    if (!suEmail.trim()) errs.suEmail = "Email is required";
    if (!pwValid) errs.suPassword = "Password does not meet all requirements";
    if (suConfirm !== suPassword) errs.suConfirm = "Passwords do not match";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    // Submit the sign in data
    var formData = {
      email: suEmail,
      password: suPassword,
      firstName: suFirstName,
      lastName: suLastName,
      newsletter: marketing,
    };
    dispatch(signup(formData, onClose, navigate, setErrorMessage));
    if (formData.newsletter) {
      await addtonewsletter(formData);
    }
  };

  const handleGoogleSuccess = (res) => {
    console.log("Google auth success:", res);
    dispatch(signuporingoogle(res.credential, navigate, setErrorMessage));
    onClose();
  };

  return (
    <div
      className="auth-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={mode === "signin" ? "Sign in" : "Create account"}
    >
      <div className="auth-panel">
        <button
          className="auth-panel__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {mode === "signin" ? (
          <>
            <h2 className="auth-panel__title">Sign in</h2>

            <form className="auth-form" onSubmit={handleSignIn} noValidate>
              {errorMessage && (
                <div className="auth-error-banner" role="alert">
                  <span>{errorMessage}</span>
                  <button
                    type="button"
                    className="auth-error-banner__close"
                    onClick={() => setErrorMessage("")}
                    aria-label="Dismiss error"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="auth-field">
                <label className="auth-field__label" htmlFor="si-email">
                  Email address
                </label>
                <input
                  id="si-email"
                  name="email"
                  className={`auth-field__input${errors.siEmail ? " auth-field__input--error" : ""}`}
                  type="email"
                  autoComplete="username"
                  value={siEmail}
                  onChange={(e) => setSiEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                {errors.siEmail && (
                  <span className="auth-field__error">{errors.siEmail}</span>
                )}
              </div>

              <div className="auth-field">
                <label className="auth-field__label" htmlFor="si-password">
                  Password
                </label>
                <div className="auth-field__pw-wrap">
                  <input
                    id="si-password"
                    name="password"
                    className={`auth-field__input${errors.siPassword ? " auth-field__input--error" : ""}`}
                    type={siShowPw ? "text" : "password"}
                    autoComplete="current-password"
                    value={siPassword}
                    onChange={(e) => setSiPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="auth-field__pw-toggle"
                    onClick={() => setSiShowPw((v) => !v)}
                    aria-label={siShowPw ? "Hide password" : "Show password"}
                  >
                    <EyeIcon open={siShowPw} />
                  </button>
                </div>
                {errors.siPassword && (
                  <span className="auth-field__error">{errors.siPassword}</span>
                )}
              </div>

              <button
                type="button"
                className="auth-form__forgot"
                onClick={() => switchMode("forgot", { fpEmail: siEmail })}
              >
                Forgot password?
              </button>

              <button
                type="submit"
                className="btn btn--primary btn--full auth-form__submit"
              >
                Sign in
              </button>
            </form>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <div className="auth-google">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => console.error("Google login failed")}
                theme="outline"
                logo_alignment="center"
                width={360}
                ux_mode="popup"
              />
            </div>

            <p className="auth-switch">
              Don't have an account?{" "}
              <button
                type="button"
                className="auth-switch__btn"
                onClick={() => switchMode("signup")}
              >
                Sign up
              </button>
            </p>
          </>
        ) : mode === "signup" ? (
          <>
            <h2 className="auth-panel__title">Create account</h2>

            <form className="auth-form" onSubmit={handleSignUp} noValidate>
              {errorMessage && (
                <div className="auth-error-banner" role="alert">
                  <span>{errorMessage}</span>
                  <button
                    type="button"
                    className="auth-error-banner__close"
                    onClick={() => setErrorMessage("")}
                    aria-label="Dismiss error"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="auth-field-row">
                <div className="auth-field">
                  <label className="auth-field__label" htmlFor="su-first">
                    First name
                  </label>
                  <input
                    id="su-first"
                    className={`auth-field__input${errors.suFirstName ? " auth-field__input--error" : ""}`}
                    type="text"
                    autoComplete="given-name"
                    value={suFirstName}
                    onChange={(e) => setSuFirstName(e.target.value)}
                    placeholder="Jane"
                  />
                  {errors.suFirstName && (
                    <span className="auth-field__error">
                      {errors.suFirstName}
                    </span>
                  )}
                </div>
                <div className="auth-field">
                  <label className="auth-field__label" htmlFor="su-last">
                    Last name
                  </label>
                  <input
                    id="su-last"
                    className={`auth-field__input${errors.suLastName ? " auth-field__input--error" : ""}`}
                    type="text"
                    autoComplete="family-name"
                    value={suLastName}
                    onChange={(e) => setSuLastName(e.target.value)}
                    placeholder="Doe"
                  />
                  {errors.suLastName && (
                    <span className="auth-field__error">
                      {errors.suLastName}
                    </span>
                  )}
                </div>
              </div>

              <div className="auth-field">
                <label className="auth-field__label" htmlFor="su-email">
                  Email address
                </label>
                <input
                  id="su-email"
                  name="email"
                  className={`auth-field__input${errors.suEmail ? " auth-field__input--error" : ""}`}
                  type="email"
                  autoComplete="email"
                  value={suEmail}
                  onChange={(e) => {
                    setSuEmail(e.target.value);
                  }}
                  placeholder="you@example.com"
                />
                {errors.suEmail && (
                  <span className="auth-field__error">{errors.suEmail}</span>
                )}
              </div>

              <div className="auth-field">
                <label className="auth-field__label" htmlFor="su-password">
                  Password
                </label>
                <div className="auth-field__pw-wrap">
                  <input
                    id="su-password"
                    name="new-password"
                    className={`auth-field__input${errors.suPassword ? " auth-field__input--error" : ""}`}
                    type={suShowPw ? "text" : "password"}
                    autoComplete="new-password"
                    value={suPassword}
                    onChange={(e) => setSuPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="auth-field__pw-toggle"
                    onClick={() => setSuShowPw((v) => !v)}
                    aria-label={suShowPw ? "Hide password" : "Show password"}
                  >
                    <EyeIcon open={suShowPw} />
                  </button>
                </div>
                {errors.suPassword && (
                  <span className="auth-field__error">{errors.suPassword}</span>
                )}

                <ul className="auth-pw-rules">
                  {pwChecks.map((c) => {
                    const hasInput = suPassword.length > 0;
                    const state = hasInput ? (c.passed ? "ok" : "fail") : "";
                    return (
                      <li
                        key={c.id}
                        className={`auth-pw-rule${state ? ` auth-pw-rule--${state}` : ""}`}
                      >
                        <span className="auth-pw-rule__icon" aria-hidden="true">
                          {hasInput ? (c.passed ? "✓" : "✗") : "·"}
                        </span>
                        {c.label}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="auth-field">
                <label className="auth-field__label" htmlFor="su-confirm">
                  Confirm password
                </label>
                <input
                  id="su-confirm"
                  name="confirm-password"
                  className={`auth-field__input${errors.suConfirm ? " auth-field__input--error" : ""}`}
                  type={suShowPw ? "text" : "password"}
                  autoComplete="new-password"
                  value={suConfirm}
                  onChange={(e) => setSuConfirm(e.target.value)}
                  placeholder="••••••••"
                />
                {errors.suConfirm && (
                  <span className="auth-field__error">{errors.suConfirm}</span>
                )}
              </div>

              <label className="auth-checkbox">
                <input
                  type="checkbox"
                  className="auth-checkbox__input"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                />
                <span className="auth-checkbox__box" aria-hidden="true" />
                <span className="auth-checkbox__label">
                  Yes, I want to receive occasional emails about new courses and
                  features
                </span>
              </label>

              <button
                type="submit"
                className="btn btn--primary btn--full auth-form__submit"
              >
                Create account
              </button>
            </form>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <div className="auth-google">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => console.error("Google signup failed")}
                theme="outline"
                logo_alignment="center"
                width={360}
                ux_mode="popup"
              />
            </div>

            <p className="auth-switch">
              Already have an account?{" "}
              <button
                type="button"
                className="auth-switch__btn"
                onClick={() => switchMode("signin")}
              >
                Sign in
              </button>
            </p>
          </>
        ) : (
          /* ── Forgot password ── */
          <>
            <button
              className="auth-panel__back"
              onClick={() => switchMode("signin")}
              aria-label="Back to sign in"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </button>

            <h2 className="auth-panel__title">Reset password</h2>

            {fpSubmitted ? (
              <div className="auth-forgot-success">
                <div className="auth-forgot-success__icon" aria-hidden="true">
                  ✉
                </div>
                <p className="auth-forgot-success__heading">Check your inbox</p>
                <p className="auth-forgot-success__body">
                  We've sent reset instructions to <strong>{fpEmail}</strong>.
                </p>
                <button
                  type="button"
                  className="btn btn--ghost btn--full"
                  style={{ marginTop: "1.5rem" }}
                  onClick={() => switchMode("signin")}
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <form
                className="auth-form"
                onSubmit={handleForgotSubmit}
                noValidate
              >
                <p className="auth-forgot-hint">
                  Enter the email address for your account and we'll send you a
                  link to reset your password.
                </p>
                <div className="auth-field">
                  <label className="auth-field__label" htmlFor="fp-email">
                    Email address
                  </label>
                  <input
                    id="fp-email"
                    className={`auth-field__input${errors.fpEmail ? " auth-field__input--error" : ""}`}
                    type="email"
                    autoComplete="email"
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoFocus
                  />
                  {errors.fpEmail && (
                    <span className="auth-field__error">{errors.fpEmail}</span>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn btn--primary btn--full auth-form__submit"
                >
                  Send reset link
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
